import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Star, MessageSquareQuote, Send, Quote } from "lucide-react";
import api from "../lib/api";
import { extractApiError } from "../lib/errors";

/* -------------------------------------------------------------------------- */
/*  Infinite vertical-scroll testimonial column                                */
/*                                                                            */
/*  Behaviour:                                                                */
/*   • Pulls /api/reviews/published once, falls back to a couple of fresh     */
/*     locally-pushed items when a visitor submits via the inline form.       */
/*   • Renders the list TWICE inside a vertically masked track and lets a     */
/*     CSS keyframe translate Y by -50% in an infinite loop — the second     */
/*     copy seamlessly continues where the first ends.                        */
/*   • If the published list is short (< 4 items) we repeat it enough times   */
/*     that the column stays packed and never shows blank vertical gaps.      */
/*   • Hovering pauses the marquee so people can read longer testimonies.    */
/* -------------------------------------------------------------------------- */
function TestimonialScroller({ items, freshIds }) {
  const trackRef = useRef(null);
  const [duration, setDuration] = useState(40);

  // Pack the column — repeat until we have at least 6 cards.
  const packed = useMemo(() => {
    if (!items || items.length === 0) return [];
    let out = [...items];
    while (out.length < 6) out = [...out, ...items];
    return out;
  }, [items]);

  // Duplicate the packed list so translateY(-50%) loops seamlessly.
  const looped = useMemo(() => [...packed, ...packed], [packed]);

  // Recompute animation duration from the rendered track height so the
  // scroll speed feels consistent regardless of how many items render.
  useEffect(() => {
    const recalc = () => {
      if (!trackRef.current) return;
      const halfHeight = trackRef.current.scrollHeight / 2;
      // ~22 px / second — gentle, easy-to-read upward drift.
      setDuration(Math.max(30, halfHeight / 22));
    };
    recalc();
    const ro = new ResizeObserver(recalc);
    if (trackRef.current) ro.observe(trackRef.current);
    return () => ro.disconnect();
  }, [looped.length]);

  if (packed.length === 0) {
    return (
      <p
        data-testid="published-reviews-empty"
        className="text-sm text-[#C5D1EB]/60 bg-white/[0.03] backdrop-blur-md border border-white/10 p-6"
      >
        Be the first to share — your story can be on air.
      </p>
    );
  }

  return (
    <div
      data-testid="testimonial-scroller"
      className="vmarquee-mask absolute inset-0 overflow-hidden"
    >
      <div
        ref={trackRef}
        className="absolute left-0 right-0 top-0 flex flex-col gap-3"
        style={{
          animation: `vmarquee-scroll ${duration}s linear infinite`,
          willChange: "transform",
        }}
      >
        {looped.map((r, idx) => (
          <article
            key={`${r.id}-${idx}`}
            data-testid={`testimonial-card-${r.id}`}
            className="relative bg-white/[0.04] backdrop-blur-md border border-white/10 hover:border-white/25 p-5 transition-colors"
          >
            <Quote
              size={14}
              strokeWidth={2}
              className="text-[#C5001A]"
              aria-hidden
            />
            <p className="mt-2 text-sm text-white leading-relaxed line-clamp-5">
              {r.review_text}
            </p>
            <div className="mt-3 flex items-center justify-between gap-2">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#C5D1EB]/75 truncate">
                {r.name}
                {r.location ? ` · ${r.location}` : ""}
              </p>
              <div className="flex items-center gap-0.5 text-[#C5001A] shrink-0">
                {[...Array(Math.max(1, Math.min(5, r.rating || 5)))].map(
                  (_, i) => (
                    <Star key={i} size={10} fill="currentColor" />
                  )
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Visitor-facing review form + infinite scroller (composite section)        */
/* -------------------------------------------------------------------------- */
export default function ReviewsBlock({
  heading = "Share your experience with our ministry",
}) {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [rating, setRating] = useState(5);
  const [review_text, setText] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [published, setPublished] = useState([]);
  const [freshIds, setFreshIds] = useState(() => new Set());

  const loadPublished = async () => {
    try {
      const { data } = await api.get("/reviews/published");
      setPublished(data.slice(0, 20));
    } catch {
      /* no-op */
    }
  };

  useEffect(() => {
    loadPublished();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please add your name.");
    if (review_text.trim().length < 8)
      return toast.error("Your review needs at least a sentence.");
    if (!consent)
      return toast.error(
        "Please confirm the consent so we can moderate appropriately."
      );
    try {
      setLoading(true);
      const { data: created } = await api.post("/reviews", {
        name: name.trim(),
        location: location.trim(),
        rating,
        review_text: review_text.trim(),
        consent_to_edit: consent,
      });

      // Optimistically prepend so the marquee shows the new review
      // immediately — even before the moderation queue publishes it.
      const optimistic = {
        id:
          (created && created.id) ||
          `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: name.trim(),
        location: location.trim(),
        rating,
        review_text: review_text.trim(),
      };
      setPublished((prev) => [optimistic, ...prev]);
      setFreshIds((prev) => {
        const next = new Set(prev);
        next.add(optimistic.id);
        return next;
      });

      toast.success("Thank you — your review has been received.");
      setName("");
      setLocation("");
      setText("");
      setRating(5);
      setConsent(false);
    } catch (er) {
      toast.error(extractApiError(er, "Could not submit review."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="reviews-block"
      className="relative overflow-hidden border-t border-white/5 bg-[#0B0C10]"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 right-1/3 w-[36rem] h-[36rem] rounded-full opacity-20 blur-[120px]"
        style={{ background: "radial-gradient(circle, #C5001A, transparent 70%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-6 items-stretch">
          {/* Submit */}
          <form
            onSubmit={submit}
            data-testid="review-form"
            className="lg:col-span-7 bg-white/[0.04] backdrop-blur-md border border-white/10 p-7 sm:p-9 flex flex-col"
          >
            <div className="flex items-center gap-3">
              <MessageSquareQuote size={20} className="text-[#C5001A]" />
              <h2 className="font-display text-2xl sm:text-3xl text-white tracking-tight">
                {heading}
              </h2>
            </div>
            <p className="mt-2 text-sm text-[#C5D1EB]">
              A line or two about what God's doing in your life through the
              ministry.
            </p>

            <div className="mt-7 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                  Name
                </label>
                <input
                  data-testid="review-name"
                  className="brand-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                  Location
                </label>
                <input
                  data-testid="review-location"
                  className="brand-input"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="City, Country"
                />
              </div>
            </div>

            <div className="mt-5">
              <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                Rating
              </label>
              <div className="flex gap-1.5" data-testid="review-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    type="button"
                    key={n}
                    onClick={() => setRating(n)}
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                    className={`w-9 h-9 inline-flex items-center justify-center border transition-colors ${
                      n <= rating
                        ? "border-[#C5001A] text-[#C5001A] bg-[#C5001A]/10"
                        : "border-white/15 text-[#C5D1EB]/50"
                    }`}
                  >
                    <Star
                      size={14}
                      fill={n <= rating ? "currentColor" : "none"}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                Your review
              </label>
              <textarea
                data-testid="review-text"
                rows={4}
                className="brand-input"
                value={review_text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Briefly share your experience with Life Talk Radio or Father's House."
              />
            </div>

            <label className="mt-5 flex items-start gap-3 text-[12px] text-[#C5D1EB]/85 leading-relaxed cursor-pointer">
              <input
                type="checkbox"
                data-testid="review-consent"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 accent-[#C5001A]"
              />
              <span>
                I give consent for the Life Talk ministry staff to{" "}
                <strong className="text-white">
                  edit, moderate, or remove
                </strong>{" "}
                this review to avoid unwanted issues.
              </span>
            </label>

            <button
              data-testid="review-submit"
              disabled={loading}
              className="mt-6 inline-flex items-center gap-2 bg-[#C5001A] hover:bg-[#E31C3D] disabled:opacity-60 text-white px-6 py-3 text-sm transition-colors w-fit"
            >
              <Send size={14} /> {loading ? "Sending…" : "Share my review"}
            </button>
          </form>

          {/* Infinite scrolling testimonials column.
              Locked to the form's height via grid items-stretch + relative box
              with overflow-hidden. The actual marquee track inside is absolute-
              positioned so its tall content never inflates the container. */}
          <aside
            data-testid="published-reviews"
            className="lg:col-span-5 relative overflow-hidden bg-white/[0.03] backdrop-blur-md border border-white/10 flex flex-col min-h-[520px]"
          >
            <div className="relative z-10 px-5 sm:px-6 pt-5 sm:pt-6 pb-3 shrink-0 flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#C5D1EB]/70">
                Voices from the family
              </p>
              <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-[#C5001A]">
                <span className="live-dot" /> Live
              </span>
            </div>
            <div className="relative flex-1 min-h-0">
              <div className="absolute inset-0 px-5 sm:px-6 pb-5 sm:pb-6">
                <div className="relative w-full h-full overflow-hidden">
                  <TestimonialScroller items={published} freshIds={freshIds} />
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
